// Beyond the ring (SPEC-viewfinder §3, amending D-4 and D-46).
//
// „Chcę widzieć wszystko, ale focus mieć na liście rzeczy w promieniu" (owner,
// 2026-09-21). So: small unlabelled dots for what is outside the radius, drawn
// on the washed map, under everything a resident can reach.
//
// **What is drawn** is `derive()`'s `beyond` and nothing else — events, water
// faults, ZTM notices and the vehicles of the resident's OWN lines, because
// that set is built from `onTheRail`, which already drops every other vehicle
// (`src/derive/axis.ts`, owner's ruling 2). Handing this layer `marked` instead
// would put 246–247 vehicle dots on the washed band against 27 for everything
// else, and the context would read as the busier half of the screen. It is the
// same array the toolbar counts as „poza M", so the number and the picture are
// never built out of two different filters. On the SCREEN they differ, and by
// design: a dot stands where its item is, so one whose item projects off the
// visible map is clipped away. The count is the city's; the dots are the ones
// with room on the map (§3).
//
// **Not interactive**: `aria-hidden`, no tab stop, no pointer. They are
// scenery, like the other-vehicle dots (D-f, §5.6). Nothing on the list
// corresponds to one, so a dot that could be tapped would promise a row that
// does not exist.
//
// No cap and no clustering: 27 dots at the whole-city worst case, measured on
// live data 2026-09-20 (§3).
import type { Item } from '../shared/model.ts';

/** Where a lon/lat lands in the overlay's pixels. The camera's, handed in. */
export type Project = (lon: number, lat: number) => { x: number; y: number };

/**
 * Rebuilds the layer from the settled `beyond`. Called from the chrome half of
 * a paint, never per frame: a gesture pushes its own `beyond` through
 * `src/state/gesture.ts` and this runs for that too, because the set really
 * does change under the finger — an item the ring passes over stops being a dot
 * and becomes a mark in the same frame.
 */
export function paintBeyond(layer: HTMLElement, items: Item[], project: Project): void {
  // The settled path reads the layer to find out what it is holding. The live
  // path does not — it carries the map across frames, because reading
  // `dataset` off a few hundred elements seven times a second was a measurable
  // share of this session's worst frames (§4.3).
  const had = new Map<string, HTMLElement>();
  for (const dot of layer.children as HTMLCollectionOf<HTMLElement>) {
    if (dot.dataset.item) had.set(dot.dataset.item, dot);
  }
  syncBeyond(layer, items, project, had);
}

/**
 * The same drawing, against a map of what is already there that the CALLER
 * owns and keeps between frames.
 *
 * Incremental, not `replaceChildren`: under a finger this is asked every time
 * the ring passes over something, and throwing away forty dots to build
 * thirty-nine of them again is layout work a 6× throttled frame has no room
 * for. A dot that stays is not touched at all — `translateBeyond` moves it with
 * the camera like everything else on the overlay.
 */
export function syncBeyond(
  layer: HTMLElement,
  items: Item[],
  project: Project,
  had: Map<string, HTMLElement>,
): void {
  const wanted = new Set<string>();
  for (const item of items) {
    if (!item.position) continue;
    wanted.add(item.id);
    if (had.has(item.id)) continue;
    const dot = document.createElement('div');
    dot.className = 'circle__beyond';
    // Scenery: out of the accessibility tree entirely, and out of the way of
    // every pointer. The layer says it too, so a dot cannot be reached even if
    // one of these is ever forgotten on an element.
    dot.setAttribute('aria-hidden', 'true');
    // The ground it stands on, in the dataset, exactly as a mark carries it —
    // which is what lets one re-projection loop move both.
    dot.dataset.item = item.id;
    dot.dataset.lon = String(item.position.lon);
    dot.dataset.lat = String(item.position.lat);
    const at = project(item.position.lon, item.position.lat);
    dot.style.transform = `translate(${at.x}px, ${at.y}px)`;
    layer.append(dot);
    had.set(item.id, dot);
  }
  for (const [id, dot] of had) {
    if (wanted.has(id)) continue;
    dot.remove();
    had.delete(id);
  }
}

/**
 * Puts the dots back where the camera now says they belong. Per frame, and it
 * is the whole of their per-frame cost: one `project` and one transform each,
 * no measuring, no rebuild (§4.3).
 */
export function translateBeyond(layer: HTMLElement, project: Project): void {
  for (const dot of layer.children as HTMLCollectionOf<HTMLElement>) {
    const lon = Number(dot.dataset.lon);
    const lat = Number(dot.dataset.lat);
    if (!Number.isFinite(lon) || !Number.isFinite(lat)) continue;
    const at = project(lon, lat);
    dot.style.transform = `translate(${at.x}px, ${at.y}px)`;
  }
}
